import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../../contexts/LanguageContext";
import { V2, pickLang } from "../copy";
import { route } from "../paths";
import { Arrow, Display, Eyebrow } from "./Primitives";

interface Props {
  /** Full list, no sticky header — used by the standalone FAQ page. */
  standalone?: boolean;
}

const FAQV2: React.FC<Props> = ({ standalone = false }) => {
  const { currentLanguage } = useLanguage();
  const lang = pickLang(currentLanguage);
  const c = V2[lang].faq;
  const [open, setOpen] = useState<number | null>(0);
  const items = standalone ? [...c.items, ...c.extra] : c.items;

  const list = (
    <div className="v2-reveal" data-delay="2">
      {items.map(([q, a], i) => {
        const isOpen = open === i;
        return (
          <div key={i} className="border-t border-ink/15 last:border-b">
            <button
              onClick={() => setOpen(isOpen ? null : i)}
              className="group flex w-full items-start justify-between gap-6 py-6 text-left"
              aria-expanded={isOpen}
            >
              <span className="flex gap-4 sm:gap-6">
                <span className="pt-2 font-mono text-[11px] tracking-[0.22em] text-ink/40">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-display text-2xl leading-tight sm:text-3xl">
                  {q}
                </span>
              </span>
              <span
                className={`mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-ink/20 transition-all duration-500 group-hover:bg-ink group-hover:text-bone ${
                  isOpen ? "rotate-45 bg-ink text-bone" : ""
                }`}
                aria-hidden
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.6">
                  <path d="M12 5v14M5 12h14" />
                </svg>
              </span>
            </button>
            <div className={`v2-faq-body ${isOpen ? "is-open" : ""}`}>
              <div>
                <p className="max-w-2xl pb-7 pl-[calc(1.5rem+1.25rem)] text-ink/65 sm:pl-[calc(2.25rem+1.5rem)]">
                  {a}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  if (standalone) {
    return (
      <section className="bg-bone py-16 text-ink sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">{list}</div>
      </section>
    );
  }

  return (
    <section id="v2-faq" className="bg-bone py-24 text-ink sm:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-10 px-4 sm:px-6 lg:grid-cols-12">
        <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
          <Eyebrow index="07">{c.label}</Eyebrow>
          <Display className="mt-5 text-[clamp(2.4rem,5.5vw,5rem)]">
            {c.title}
          </Display>
          <Link
            to={route(lang, "faq")}
            className="v2-link v2-reveal mt-6 inline-flex items-center gap-2 text-sm"
            data-delay="2"
          >
            {c.more}
            <Arrow />
          </Link>
        </div>
        <div className="lg:col-span-8">{list}</div>
      </div>
    </section>
  );
};

export default FAQV2;
